/**
 * BNPL financing screen (ERS-5, merged in ERS-61, rebuilt as screen 07 in ERS-78)
 *
 * Route: `/webAppOnboarding/insurance-bnpl-process-overview`. Cost-confirmation step for the BNPL
 * billing path: hero, who-pays intro, price card, cost breakdown, and benefits. Mirrors the Figma
 * `Onboarding-BNPL-Financing-*` frames, whose two variants are full vs. partial refund (the bonus
 * benefit row is a third, independent axis). The "So funktioniert's" detail drawer moved to the
 * payment screen (09); this screen no longer opens it.
 *
 * Before ERS-61 this content lived on two screens (`insurance-bnpl-process-overview` timeline plus
 * `insurance-bnpl-payment-overview` hero/table); the latter is gone and its route redirects here.
 *
 * Guards:
 * - Route: `OnboardingSignUpGateLayout` → `OnboardingAuthGuard` → `OnboardingCompletedGuard`.
 * - Auth: Firebase user required via `OnboardingAuthGuard`.
 * - Onboarding state: completed patients redirected to install by `OnboardingCompletedGuard`.
 * - Screen-local: `useInsuranceExplainBillingGuard('bnpl')` — wrong/missing billing type → insurance-select
 *   or matching explain route; `useSaveOnboardingStep('insurance_bnpl_process_overview')`.
 */

import { PATIENT_TRACKING_EVENTS } from '@physiofit-digital/physiofit_types';
import { usePostHog } from '@posthog/react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { colors, getMediumRGBA } from '../../../styles/colors';
import { radii, space } from '../../../styles/onboardingUiTokens';
import { OnboardingHeroBanner } from '../components/content/OnboardingHeroBanner';
import { OnboardingCheckoutTable } from '../components/content/OnboardingCheckoutTable';
import { OnboardingPriceCard } from '../components/content/OnboardingPriceCard';
import { CARD_TABLE_COLORS } from '../components/content/checkoutTableColors';
import { LoadingSpinner } from '../components/elements/LoadingSpinner';
import { Notice } from '../components/elements/Notice';
import { OnboardingBody } from '../components/layout/OnboardingBody';
import { OnboardingFooter } from '../components/layout/OnboardingFooter';
import { OnboardingHeader } from '../components/layout/OnboardingHeader';
import { OnboardingLayout } from '../components/layout/OnboardingLayout';
import { Body } from '../components/typography/Body';
import { H1, H3 } from '../components/typography/HeadingLevels';
import { List, ListItem } from '../components/typography/List';
import { checkMarker } from '../components/typography/listMarkers';
import { REQUIRED_COURSE_UNITS } from '../hooks/useCourseContent';
import { useCourseZppInfo } from '../hooks/useCourseZppInfo';
import { useInsuranceExplainBillingGuard } from '../hooks/useInsuranceExplainBillingGuard';
import { useSelectedInsurance } from '../hooks/useSelectedInsurance';
import {
  BNPL_COST_TABLE_LABELS,
  BNPL_FINANCING_BENEFITS_HEADING,
  BNPL_FINANCING_CTA,
  BNPL_FINANCING_HEADLINE,
  buildBnplBonusBenefit,
  buildBnplCourseLegalFootnote,
  buildBnplFinancingBenefits,
  buildBnplFinancingIntro,
  buildBnplPriceCardCopy,
} from '../insurance/bnplCopy';
import { formatEuro } from '../insurance/copyUtils';
import { displayInsuranceName, displayInsuranceShortName } from '../insurance/displayInsuranceName';
import { isPatientSubmittingInsurer, resolveCourseFeeEur, resolveInsuranceCoverage } from '../insurance/metaBuilders';
import { resolveInsuranceOnboardingContext } from '../insurance/routing';
import { persistDisplayedBillingInfo, useSaveOnboardingStep } from '../services/onboardingProgress';
import { getSignedUpPatient, useOnboardingStore } from '../store/onboardingStore';

export const ONBOARDING_SCREEN_META = {
  requiresFirebaseSession: true,
} as const;

const ContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space.sm};
`;

/**
 * Heading + content block. The headings need real air on both sides — the column gap alone left
 * them crowded between the cost card and their own list (ERS-61 feedback).
 */
const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space.lg};
  margin-top: ${space.md};
`;

const DynamicLoadingArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 12rem;
  width: 100%;
`;

const PriceContainer = styled.div`
  border: 1px solid ${getMediumRGBA(0.1)};
  border-radius: ${radii.lg};
  background: ${colors.white};
  overflow: hidden;
`;

export function WebOnboardingInsuranceBnplProcessOverviewScreen() {
  useSaveOnboardingStep('insurance_bnpl_process_overview');
  useInsuranceExplainBillingGuard('bnpl');
  const navigate = useNavigate();
  const posthog = usePostHog();
  const signedUpPatient = useOnboardingStore(getSignedUpPatient);
  const { insurance, loading: insuranceLoading, error: insuranceError } = useSelectedInsurance();
  const displayedBillingPersistedRef = useRef(false);

  const ctx = useMemo(
    () => resolveInsuranceOnboardingContext({ source: 'patient', patient: signedUpPatient }),
    [signedUpPatient],
  );

  const contentCourseId = signedUpPatient?.course?.id?.trim() ?? '';
  const courseInfo = useCourseZppInfo(contentCourseId, insurance ? resolveCourseFeeEur(insurance) : undefined);

  const insuranceName = useMemo(() => {
    if (insurance) {
      return displayInsuranceName(insurance);
    }
    return ctx.insuranceName ?? '';
  }, [insurance, ctx.insuranceName]);

  const insuranceShortName = useMemo(() => {
    if (insurance) {
      return displayInsuranceShortName(insurance) || insuranceName;
    }
    return insuranceName;
  }, [insurance, insuranceName]);

  const coverage = useMemo(() => (insurance ? resolveInsuranceCoverage(insurance) : null), [insurance]);

  const hasBonus = Boolean(insurance?.bonus?.hasBonus);

  useEffect(() => {
    if (!insurance || !coverage || displayedBillingPersistedRef.current) {
      return;
    }
    displayedBillingPersistedRef.current = true;
    persistDisplayedBillingInfo(insurance);
  }, [insurance, coverage]);

  const handleBack = useCallback(() => {
    navigate('/webAppOnboarding/insurance-select');
  }, [navigate]);

  const handleForward = useCallback(() => {
    posthog.capture(PATIENT_TRACKING_EVENTS.web_insurance_process_overview_completed, {
      processType: 'bnpl',
      insuranceId: insurance?.id,
      billingType: signedUpPatient?.webAppOnboarding?.insuranceInfo?.billingType,
    });
    if (ctx.forwardPath) {
      navigate(ctx.forwardPath);
    }
  }, [
    posthog,
    insurance?.id,
    signedUpPatient?.webAppOnboarding?.insuranceInfo?.billingType,
    ctx.forwardPath,
    navigate,
  ]);

  const courseFootnote = useMemo(() => {
    if (courseInfo.status !== 'ready') {
      return '';
    }
    return buildBnplCourseLegalFootnote(courseInfo.designation, courseInfo.zppId);
  }, [courseInfo]);

  const bodyContent = (() => {
    if (insuranceLoading) {
      return (
        <DynamicLoadingArea>
          <LoadingSpinner color={colors.primary} size={32} />
        </DynamicLoadingArea>
      );
    }

    if (insuranceError) {
      return (
        <Notice preset="warning" title="Krankenkasse">
          {insuranceError}
        </Notice>
      );
    }

    if (ctx.billingType !== 'bnpl' || !insurance || !insuranceName || !coverage) {
      return (
        <Notice preset="warning" title="Krankenkasse">
          Bitte wähle zuerst deine Krankenkasse aus.
        </Notice>
      );
    }

    if (contentCourseId && courseInfo.status === 'loading') {
      return (
        <DynamicLoadingArea>
          <LoadingSpinner color={colors.primary} size={32} />
        </DynamicLoadingArea>
      );
    }

    return (
      <ContentColumn>
        <H1>{BNPL_FINANCING_HEADLINE}</H1>
        <Body variant="document" tone="muted">
          {buildBnplFinancingIntro(insuranceName, coverage)}
        </Body>
        <PriceContainer>
          <OnboardingPriceCard {...buildBnplPriceCardCopy(coverage)} />

          <OnboardingCheckoutTable
            showFooter
            showPhysiofitSubsidy={!coverage.isFullCoverage}
            colors={CARD_TABLE_COLORS}
            copy={{
              a11ySummary: BNPL_COST_TABLE_LABELS.a11ySummary,
              courseFeeLabel: BNPL_COST_TABLE_LABELS.courseFee(REQUIRED_COURSE_UNITS),
              courseFeeValue: formatEuro(coverage.courseFeeEur, 'plain'),
              insurerRefundLabel: BNPL_COST_TABLE_LABELS.insurerRefund,
              insurerRefundValue: formatEuro(coverage.insurerRefundEur, 'deduct'),
              physiofitSubsidyLabel: BNPL_COST_TABLE_LABELS.physiofitSubsidy,
              physiofitSubsidyValue: formatEuro(coverage.physiofitSubsidyEur, 'deduct'),
              footerLabel: BNPL_COST_TABLE_LABELS.yourPrice,
              footerValue: formatEuro(0, 'plain'),
            }}
          />
        </PriceContainer>

        <Section>
          <H3>{BNPL_FINANCING_BENEFITS_HEADING}</H3>
          <List
            {...checkMarker}
            icon={{ ...checkMarker.icon, size: 12 }}
            iconBoxSize="1.25rem"
            iconBoxed
            listPadding="0"
            rowGap={space.sm}
          >
            {buildBnplFinancingBenefits(isPatientSubmittingInsurer(insurance)).map((benefit) => (
              <ListItem key={benefit}>
                <Body variant="document" tone="muted">
                  {benefit}
                </Body>
              </ListItem>
            ))}
            {hasBonus ? (
              <ListItem>
                <Body variant="document" tone="muted">
                  {buildBnplBonusBenefit(insuranceShortName, insurance.bonus?.valueEuro)}
                </Body>
              </ListItem>
            ) : null}
          </List>
        </Section>
      </ContentColumn>
    );
  })();

  return (
    <OnboardingLayout
      currentStep={4}
      totalSteps={7}
      onBack={handleBack}
      forwardLabel={BNPL_FINANCING_CTA}
      forwardDisabled={insuranceLoading || !coverage}
      onForward={handleForward}
    >
      <OnboardingHeader />
      <OnboardingHeroBanner />
      <OnboardingBody
        afterSupportHint={
          courseFootnote ? (
            <Body variant="document" size="mini" tone="muted">
              {courseFootnote}
            </Body>
          ) : null
        }
      >
        <div style={{ paddingBottom: space.xl }}>{bodyContent}</div>
      </OnboardingBody>
      <OnboardingFooter />
    </OnboardingLayout>
  );
}
